import { CanvasElement } from '@/lib/engineTypes';
import type { PromptState, PromptSections, PromptSettings } from '@/store/promptStore';
import { resolveAllPromptIssues } from '@/lib/promptInlineAnalyzer';

const normalizeWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

const stableStringify = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value !== 'object') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  const objectValue = value as Record<string, unknown>;
  const keys = Object.keys(objectValue).sort();
  return `{${keys.map((key) => `${key}:${stableStringify(objectValue[key])}`).join(',')}}`;
};

const composeCanvasSegment = (elements: CanvasElement[]): string => {
  if (!elements.length) {
    return '';
  }

  return elements
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((element) => {
      const x = Number(element.x.toFixed(2));
      const y = Number(element.y.toFixed(2));
      const scale = Number(element.scale.toFixed(2));
      return `${element.id}:${element.type}@(${x},${y})#${scale}`;
    })
    .join(' | ');
};

const composeObjectSegment = (label: string, value: PromptSections | PromptSettings): string => {
  if (!Object.keys(value).length) {
    return '';
  }

  return `${label}: ${stableStringify(value)}`;
};

export const composePrompt = (state: PromptState): string => {
  const base = normalizeWhitespace(state.basePrompt);
  const canvasSegment = composeCanvasSegment(state.canvasElements);
  const sectionsSegment = composeObjectSegment('sections', state.sections);
  const settingsSegment = composeObjectSegment('settings', state.settings);

  const segments = [base, canvasSegment, sectionsSegment, settingsSegment].filter((segment) => segment.length > 0);

  return segments.join('\n');
};

export const enhance = (prompt: string): string => {
  const conflictResolved = resolveAllPromptIssues(prompt);

  const deduplicated = conflictResolved
    .split(',')
    .map((part) => normalizeWhitespace(part))
    .filter(Boolean)
    .filter((part, index, all) => all.findIndex((candidate) => candidate.toLowerCase() === part.toLowerCase()) === index)
    .join(', ');

  return deduplicated
    .replace(/\bnice image\b/gi, 'cinematic scene with expressive visual storytelling')
    .replace(/\bgood quality\b/gi, 'ultra-detailed 8k composition with professional grading')
    .replace(/\bbeautiful scene\b/gi, 'visually rich scene with layered depth and cinematic lighting')
    .replace(/\s{2,}/g, ' ')
    .trim();
};
